// 15E — Smart Company Onboarding & Autofill.
// Visura Camerale: upload del PDF posseduto → estrazione → validazione →
// anteprima diff → conferma/correzione → anagrafica + sedi + dato canonico.
// Bollette energia/gas: upload multiplo → analisi copertura annuale (gap,
// duplicati, sovrapposizioni, anomalie) → conferma → Environmental Engine.
// "Carica quello che hai. Rispondi solo a quello che manca."
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FileSearch, Upload, CheckCircle2, XCircle, AlertTriangle, Zap, Building2,
  FileCheck2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ACTION_LABELS = {
  FILL: { label: "Nuovo", cls: "bg-green-50 text-green-800 border-green-300" },
  MATCH: { label: "Già presente", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  OVERWRITE_CANDIDATE: { label: "Diverso", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  SKIP: { label: "Assente", cls: "bg-slate-50 text-slate-400 border-slate-200" },
};

export default function OnboardingAutofill() {
  const { companyId, periods } = useTenant();
  const [status, setStatus] = useState(null);
  const [visure, setVisure] = useState([]);
  const [preview, setPreview] = useState(null);
  const [applyFields, setApplyFields] = useState([]);
  const [applySites, setApplySites] = useState([]);
  const [uploadingVisura, setUploadingVisura] = useState(false);
  const [bollette, setBollette] = useState(null);
  const [siteMap, setSiteMap] = useState({});
  const [uploadingBollette, setUploadingBollette] = useState(false);
  const period = useMemo(
    () => [...(periods || [])].sort((a, b) => b.year - a.year)[0], [periods]);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const [st, ls] = await Promise.all([
        api.get(`/companies/${companyId}/onboarding/status`),
        api.get(`/companies/${companyId}/visura/extractions`),
      ]);
      setStatus(st.data);
      setVisure(ls.data || []);
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  }, [companyId]);

  const loadBollette = useCallback(async () => {
    if (!companyId || !period) return;
    try {
      const { data } = await api.get(
        `/companies/${companyId}/periods/${period.id}/bollette/analysis`);
      setBollette(data);
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  }, [companyId, period]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadBollette(); }, [loadBollette]);

  const uploadVisura = async (file) => {
    if (!file) return;
    setUploadingVisura(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/companies/${companyId}/visura/upload`, fd);
      toast.success("Visura analizzata", {
        description: "Controlla l'anteprima e conferma i dati." });
      await load();
      await openPreview(data.id);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error("Upload visura fallito",
        { description: typeof d === "object" ? d?.message : formatApiError(e) });
    } finally {
      setUploadingVisura(false);
    }
  };

  const openPreview = async (extractionId) => {
    try {
      const { data } = await api.get(
        `/companies/${companyId}/visura/extractions/${extractionId}/preview`);
      setPreview(data);
      setApplyFields([]);
      setApplySites((data.preview.sites || [])
        .filter((s) => !s.already_exists).map((s) => s.site_key));
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const confirmVisura = async () => {
    try {
      const { data } = await api.post(
        `/companies/${companyId}/visura/extractions/${preview.extraction.id}/confirm`,
        { overrides: {}, apply_fields: applyFields, apply_sites: applySites });
      toast.success("Dati applicati", {
        description: `${data.company_updated.length} campi anagrafici · ${data.sites_created.length} sedi create`
          + (data.autofilled.length ? ` · autofill: ${data.autofilled.map((a) => a.datapoint_code).join(", ")}` : "") });
      setPreview(null);
      await load();
    } catch (e) {
      toast.error("Conferma fallita", { description: formatApiError(e) });
    }
  };

  const rejectVisura = async (id) => {
    try {
      await api.post(`/companies/${companyId}/visura/extractions/${id}/reject`);
      toast.success("Estrazione scartata");
      setPreview(null);
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const uploadBollette = async (files) => {
    if (!files?.length || !period) return;
    setUploadingBollette(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const { data } = await api.post(
        `/companies/${companyId}/periods/${period.id}/bollette/upload`, fd);
      const ok = data.results.filter((r) => r.status === "EXTRACTED").length;
      const dup = data.results.filter((r) => r.status === "DUPLICATE_FILE").length;
      toast.success("Bollette analizzate", {
        description: `${ok} estratte${dup ? ` · ${dup} duplicate ignorate` : ""}` });
      await loadBollette();
    } catch (e) {
      toast.error("Upload bollette fallito", { description: formatApiError(e) });
    } finally {
      setUploadingBollette(false);
    }
  };

  const confirmBollette = async () => {
    const items = (bollette?.extractions || [])
      .filter((x) => x.status === "EXTRACTED" && x.data?.consumption > 0)
      .map((x) => ({ extraction_id: x.id, site_id: siteMap[x.id] || null }));
    if (!items.length) {
      toast.error("Nessuna bolletta valida da confermare");
      return;
    }
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${period.id}/bollette/confirm`, { items });
      toast.success("Consumi registrati", {
        description: `${data.created.length} record creati · ${data.skipped.length} saltati. Alimentano il calcolo Scope 1/2.` });
      await loadBollette();
    } catch (e) {
      toast.error("Conferma fallita", { description: formatApiError(e) });
    }
  };

  if (!companyId) return null;
  const cov = bollette?.coverage;

  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <FileSearch className="w-7 h-7 text-green-700" aria-hidden="true" />
          Onboarding &amp; Autofill
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Carica quello che hai: visura camerale e bollette. ESG-PMI estrae, valida e ti chiede
          solo conferma — mai due volte lo stesso dato.
        </p>
      </div>

      <Card className="border-slate-200" data-testid="visura-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4 text-green-700" aria-hidden="true" />
            Visura Camerale
            {status && (
              <Badge variant="outline" className={status.registry.auto_fetch === "ENABLED"
                ? "bg-green-50 text-green-800 border-green-300"
                : "bg-slate-100 text-slate-500 border-slate-300"}
                data-testid="registry-status-badge">
                Recupero automatico: {status.registry.auto_fetch}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <input id="visura-file" type="file" accept="application/pdf" className="hidden"
              aria-hidden="true" onChange={(e) => uploadVisura(e.target.files?.[0])} />
            <Button className="bg-green-700 hover:bg-green-800" disabled={uploadingVisura}
              onClick={() => document.getElementById("visura-file").click()}
              data-testid="visura-upload-button">
              <Upload className="w-4 h-4 mr-2" aria-hidden="true" />
              {uploadingVisura ? "Analisi in corso…" : "Carica visura (PDF)"}
            </Button>
            <p className="text-xs text-slate-500">
              Il recupero automatico dalla Camera di Commercio sarà attivabile quando verrà
              configurato un provider dalla console piattaforma.
            </p>
          </div>
          {visure.length > 0 && (
            <div className="space-y-1.5" data-testid="visura-list">
              {visure.map((v) => (
                <div key={v.id} className="flex items-center gap-2 text-sm border-b border-slate-100 pb-1.5">
                  <FileCheck2 className="w-4 h-4 text-slate-400" aria-hidden="true" />
                  <span className="text-slate-700">{v.file_name || "recupero automatico"}</span>
                  <Badge variant="outline" className="text-[10px]"
                    data-testid={`visura-status-${v.id}`}>{v.status}</Badge>
                  <span className="text-[11px] text-slate-400">{v.created_at?.slice(0, 10)}</span>
                  {v.status === "EXTRACTED" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openPreview(v.id)}
                        data-testid={`visura-preview-${v.id}`}>Anteprima</Button>
                      <Button size="sm" variant="ghost" onClick={() => rejectVisura(v.id)}
                        data-testid={`visura-reject-${v.id}`}>
                        <XCircle className="w-3.5 h-3.5 text-red-600" aria-hidden="true" /></Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {preview && (
            <div className="rounded-lg border border-green-200 bg-green-50/40 p-4 space-y-3"
              data-testid="visura-preview-panel">
              <p className="text-sm font-semibold text-slate-900">
                Anteprima dati estratti — conferma cosa applicare
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="visura-diff-table">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-1.5 pr-3">Campo</th>
                      <th className="py-1.5 pr-3">Estratto</th>
                      <th className="py-1.5 pr-3">Attuale</th>
                      <th className="py-1.5">Azione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.fields.filter((f) => f.action !== "SKIP").map((f) => (
                      <tr key={f.field} className="border-b border-slate-100">
                        <td className="py-1.5 pr-3 text-slate-600">{f.label}</td>
                        <td className="py-1.5 pr-3 font-medium text-slate-800">{String(f.extracted ?? "—")}</td>
                        <td className="py-1.5 pr-3 text-slate-500">{String(f.current ?? "—")}</td>
                        <td className="py-1.5">
                          {f.action === "OVERWRITE_CANDIDATE" ? (
                            <label className="flex items-center gap-1.5">
                              <input type="checkbox" checked={applyFields.includes(f.field)}
                                onChange={(e) => setApplyFields((p) => e.target.checked
                                  ? [...p, f.field] : p.filter((x) => x !== f.field))}
                                data-testid={`visura-overwrite-${f.field}`} />
                              <Badge variant="outline" className={ACTION_LABELS[f.action].cls}>
                                Sovrascrivi</Badge>
                            </label>
                          ) : (
                            <Badge variant="outline" className={ACTION_LABELS[f.action].cls}
                              data-testid={`visura-action-${f.field}`}>
                              {ACTION_LABELS[f.action].label}</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.preview.sites.length > 0 && (
                <div className="space-y-1" data-testid="visura-sites-preview">
                  <p className="text-xs font-semibold text-slate-700">Sedi rilevate</p>
                  {preview.preview.sites.map((s) => (
                    <label key={s.site_key} className="flex items-center gap-2 text-xs text-slate-700">
                      <input type="checkbox" disabled={s.already_exists}
                        checked={applySites.includes(s.site_key)}
                        onChange={(e) => setApplySites((p) => e.target.checked
                          ? [...p, s.site_key] : p.filter((x) => x !== s.site_key))}
                        data-testid={`visura-site-${s.site_key}`} />
                      {s.tipologia}: {[s.indirizzo, s.cap, s.citta].filter(Boolean).join(", ")}
                      {s.already_exists && (
                        <Badge variant="outline" className="text-[9px] bg-slate-100 text-slate-500">
                          già presente</Badge>)}
                    </label>
                  ))}
                </div>
              )}
              {Object.values(preview.preview.validation || {}).some((v) => !v.ok) && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800"
                  data-testid="visura-validation-warnings">
                  {Object.entries(preview.preview.validation).filter(([, v]) => !v.ok)
                    .map(([k, v]) => <p key={k}><AlertTriangle className="inline w-3 h-3 mr-1" aria-hidden="true" />{k}: {v.message}</p>)}
                </div>
              )}
              <div className="flex gap-2">
                <Button className="bg-green-700 hover:bg-green-800" onClick={confirmVisura}
                  data-testid="visura-confirm-button">
                  <CheckCircle2 className="w-4 h-4 mr-1" aria-hidden="true" /> Conferma e applica
                </Button>
                <Button variant="outline" onClick={() => rejectVisura(preview.extraction.id)}
                  data-testid="visura-discard-button">Scarta</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="bollette-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="w-4 h-4 text-green-700" aria-hidden="true" />
            Bollette energia e gas {period && `(esercizio ${period.year})`}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!period && <p className="text-sm text-slate-500">Crea prima un periodo di rendicontazione.</p>}
          {period && (
            <>
              <div className="flex items-center gap-3">
                <input id="bollette-file" type="file" accept="application/pdf" multiple
                  className="hidden" aria-hidden="true"
                  onChange={(e) => uploadBollette(Array.from(e.target.files || []))} />
                <Button className="bg-green-700 hover:bg-green-800" disabled={uploadingBollette}
                  onClick={() => document.getElementById("bollette-file").click()}
                  data-testid="bollette-upload-button">
                  <Upload className="w-4 h-4 mr-2" aria-hidden="true" />
                  {uploadingBollette ? "Analisi in corso…" : "Carica bollette (PDF, anche multiple)"}
                </Button>
                <p className="text-xs text-slate-500">
                  Carica quello che hai: la copertura annuale viene verificata sui periodi reali,
                  senza presumere 12 fatture.
                </p>
              </div>

              {cov && bollette.extractions.length > 0 && (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2" data-testid="bollette-coverage-summary">
                    <Badge variant="outline" className={cov.complete_year
                      ? "bg-green-50 text-green-800 border-green-300"
                      : "bg-amber-50 text-amber-800 border-amber-300"}>
                      Copertura {cov.year}: {cov.coverage_pct}%
                    </Badge>
                    {cov.gaps.length > 0 && (
                      <Badge variant="outline" className="bg-red-50 text-red-800 border-red-300"
                        data-testid="coverage-gaps-badge">
                        {cov.gaps.reduce((n, g) => n + g.missing_months.length, 0)} mesi mancanti
                      </Badge>
                    )}
                    {cov.duplicates.length > 0 && (
                      <Badge variant="outline" className="bg-red-50 text-red-800 border-red-300"
                        data-testid="coverage-duplicates-badge">{cov.duplicates.length} duplicati</Badge>
                    )}
                    {cov.overlaps.length > 0 && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300"
                        data-testid="coverage-overlaps-badge">{cov.overlaps.length} sovrapposizioni</Badge>
                    )}
                    {cov.anomalies.length > 0 && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300"
                        data-testid="coverage-anomalies-badge">{cov.anomalies.length} anomalie</Badge>
                    )}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="bollette-table">
                      <thead>
                        <tr className="text-left text-slate-500 border-b border-slate-200">
                          <th className="py-1.5 pr-3">File</th>
                          <th className="py-1.5 pr-3">Fornitura</th>
                          <th className="py-1.5 pr-3">POD/PDR</th>
                          <th className="py-1.5 pr-3">Periodo</th>
                          <th className="py-1.5 pr-3">Consumo</th>
                          <th className="py-1.5 pr-3">Stato</th>
                          <th className="py-1.5">Sede</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bollette.extractions.map((x) => (
                          <tr key={x.id} className="border-b border-slate-100"
                            data-testid={`bolletta-row-${x.id}`}>
                            <td className="py-1.5 pr-3 text-slate-600 max-w-[140px] truncate">{x.file_name}</td>
                            <td className="py-1.5 pr-3">{x.data?.supply_type || "—"}</td>
                            <td className="py-1.5 pr-3 font-mono">{x.data?.pod || x.data?.pdr || "—"}</td>
                            <td className="py-1.5 pr-3">
                              {x.data?.period_start && x.data?.period_end
                                ? `${x.data.period_start} → ${x.data.period_end}` : "—"}
                            </td>
                            <td className="py-1.5 pr-3 font-medium">
                              {x.data?.consumption != null ? `${x.data.consumption} ${x.data.unit || ""}` : "—"}
                            </td>
                            <td className="py-1.5 pr-3">
                              <Badge variant="outline" className="text-[10px]">{x.status}</Badge>
                            </td>
                            <td className="py-1.5">
                              {x.status === "EXTRACTED" && (
                                <Select value={siteMap[x.id] || ""}
                                  onValueChange={(v) => setSiteMap((p) => ({ ...p, [x.id]: v }))}>
                                  <SelectTrigger className="h-7 w-40 text-[11px]"
                                    data-testid={`bolletta-site-${x.id}`}>
                                    <SelectValue placeholder="Associa sede…" /></SelectTrigger>
                                  <SelectContent>
                                    {bollette.sites.map((s) => (
                                      <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>))}
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {(cov.gaps.length > 0 || cov.anomalies.length > 0) && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 space-y-0.5"
                      data-testid="bollette-warnings">
                      {cov.gaps.map((g, i) => (
                        <p key={`g${i}`}>Mesi mancanti ({g.meter}): {g.missing_months.join(", ")}</p>))}
                      {cov.anomalies.map((a, i) => (
                        <p key={`a${i}`}>{a.document}: {a.issue}</p>))}
                    </div>
                  )}
                  <Button className="bg-green-700 hover:bg-green-800" onClick={confirmBollette}
                    data-testid="bollette-confirm-button">
                    <CheckCircle2 className="w-4 h-4 mr-1" aria-hidden="true" />
                    Conferma e registra consumi
                  </Button>
                </div>
              )}
              {cov && bollette.extractions.length === 0 && (
                <p className="text-sm text-slate-500">Nessuna bolletta caricata per questo esercizio.</p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
